<template>
  <objects-search
    :metro="filter.metro"
    :how="filter.how"
    :minCost="filter.minCost"
    :maxCost="filter.maxCost"
    :minSquareMeter="filter.minSquareMeter"
    :maxSquareMeter="filter.maxSquareMeter"
    :available="filter.available"
    :type="filter.type"
    :categoryObject="filter.categoryObject"
    @newSearch="updateGetObjects"
  />

  <div v-for="item in object.objects">
    <object-card :object="item"/>
  </div>

  <pagination
    :number="10"
    :page="filter.page"
    :total="object.total"
    @newPagination="updatePage"
  />
</template>

<script>
import Pagination from "@/components/UI/Pagination.vue";
import ObjectsSearch from "@/components/pages/objects/ObjectsSearch.vue";
import ObjectCard from "@/components/pages/objects/ObjectCard.vue";
import {useRoute, useRouter} from "vue-router";
import useObjects from "@/hooks/useObjects";
import {onMounted, ref, computed} from "vue";
import {useHead} from "@unhead/vue";

export default {
  name: "objects",
  components: {Pagination, ObjectsSearch, ObjectCard},
  setup() {
    const route = useRoute()
    const router = useRouter()
    const {getObjects, object} = useObjects()

    // Инициализируем filter ДО computed-свойств
    const filter = ref({
      region: route.query.region || "",
      metro: route.query.metro || "",
      how: route.query.how || "",
      minCost: route.query.minCost || "",
      maxCost: route.query.maxCost || "",
      minSquareMeter: route.query.minSquareMeter || "",
      maxSquareMeter: route.query.maxSquareMeter || "",
      page: Number(route.query.page) || 1,
      type: route.query.type || "",
      categoryObject: route.query.categoryObject || "",
    })

    // Функция для формирования чистого URL
    const cleanQuery = () => {
      const query = {}
      if (filter.value.metro) query.metro = filter.value.metro
      if (filter.value.how) query.how = filter.value.how
      if (filter.value.minCost) query.minCost = filter.value.minCost
      if (filter.value.maxCost) query.maxCost = filter.value.maxCost
      if (filter.value.type) query.type = filter.value.type
      if (filter.value.page > 1) query.page = filter.value.page
      return query
    }

    // Теперь computed-свойства могут безопасно использовать filter
    const pageTitle = computed(() => {
      const selectedCity = localStorage.getItem('selectedCity') || 'Москве'
      let base = "P-L-E.ru — Объявления о недвижимости"

      if (filter.value.type === 'flat') base += " | Квартиры"
      else if (filter.value.type === 'house') base += " | Дома"
      else if (filter.value.type === 'commercial') base += " | Коммерческая"

      if (selectedCity === "Вся Россия") base += " по всей России"
      else base += ` в г. ${selectedCity}`

      if (filter.value.metro) base += ` у метро ${filter.value.metro}`

      return base
    })

    const pageDescription = computed(() => {
      const typeMap = {
        'flat': 'квартиры',
        'house': 'дома',
        'commercial': 'коммерческая недвижимость'
      }
      const type = typeMap[filter.value.type] || 'недвижимость'

      let desc = `Актуальные объявления о продаже ${type}`
      if (filter.value.metro) desc += ` у метро ${filter.value.metro}`
      if (filter.value.minCost || filter.value.maxCost) {
        desc += `. Цена ${filter.value.minCost ? `от ${filter.value.minCost} млн` : ''}${filter.value.maxCost ? ` до ${filter.value.maxCost} млн` : ''} руб`
      }

      return desc
    })

    const canonicalUrl = computed(() => {
      const baseUrl = 'https://p-l-e.ru/objects'
      const query = cleanQuery()
      const queryString = new URLSearchParams(query).toString()
      return queryString ? `${baseUrl}?${queryString}` : baseUrl
    })

    useHead({
      title: pageTitle,
      meta: [
        {name: 'description', content: pageDescription},
        {name: 'keywords', content: computed(() => {
            const keys = ['недвижимость', 'объявления']
            if (filter.value.type === 'flat') keys.push('квартиры', 'продажа квартир')
            if (filter.value.metro) keys.push(`метро ${filter.value.metro}`)
            return keys.join(', ')
          })},
        {property: 'og:title', content: pageTitle},
        {property: 'og:description', content: pageDescription},
        {property: 'og:url', content: canonicalUrl},
        {property: 'og:type', content: 'website'},
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'twitter:title', content: pageTitle},
        {name: 'twitter:description', content: pageDescription},
      ],
      link: [
        {rel: 'canonical', href: canonicalUrl},
        ...(filter.value.page > 1 ? [
          {rel: 'prev', href: computed(() => {
              const prevQuery = {...cleanQuery(), page: filter.value.page - 1}
              const prevQueryString = new URLSearchParams(prevQuery).toString()
              return `https://p-l-e.ru/objects?${prevQueryString}`
            })}
        ] : []),
        ...(object.value?.total > 10 * filter.value.page ? [
          {rel: 'next', href: computed(() => {
              const nextQuery = {...cleanQuery(), page: filter.value.page + 1}
              const nextQueryString = new URLSearchParams(nextQuery).toString()
              return `https://p-l-e.ru/objects?${nextQueryString}`
            })}
        ] : [])
      ],
      script: [{
        type: 'application/ld+json',
        innerHTML: computed(() => JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": object.value?.objects?.slice(0, 5).map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": filter.value.type === 'flat' ? 'Apartment' :
                filter.value.type === 'house' ? 'House' : 'Property',
              "name": item.title || 'Объект недвижимости',
              "url": `https://p-l-e.ru/objects/${item.id}`,
              "offers": {
                "@type": "Offer",
                "price": item.price,
                "priceCurrency": "RUB"
              }
            }
          })) || []
        }))
      }]
    })

    const updatePage = async (page) => {
      filter.value.page = page
      await router.replace({query: cleanQuery()})
      await getObjects()
    }

    const updateGetObjects = async (
      metro,
      how,
      minCost,
      maxCost,
      minSquareMeter,
      maxSquareMeter,
      type,
      categoryObject
    ) => {
      filter.value.metro = metro
      filter.value.how = how
      filter.value.minCost = minCost
      filter.value.maxCost = maxCost
      filter.value.minSquareMeter = minSquareMeter
      filter.value.maxSquareMeter = maxSquareMeter
      filter.value.type = type
      filter.value.categoryObject = categoryObject
      filter.value.page = 1

      await router.replace({query: cleanQuery()})
      await getObjects()
    }

    onMounted(async () => {
      await router.replace({query: cleanQuery()})
      await getObjects()
    })

    return {updateGetObjects, updatePage, object, filter}
  }
}
</script>
<style scoped>

</style>